// src/features/dashboard/components/VisitorTrendSection.tsx

import React, {
  useMemo,
} from 'react';

import {
  TrendingUp,
  Users,
} from 'lucide-react';

import type {
  VisitorTrendItem,
} from '../types/adminDashboard.types';


type VisitorTrendSectionProps = {
  data:
    VisitorTrendItem[];
};


const VisitorTrendSection:
  React.FC<
    VisitorTrendSectionProps
  > = ({
    data,
  }) => {
    /**
     * 가장 큰 방문자 수
     *
     * 막대 높이 계산 기준
     */
    const maxCount =
      useMemo(
        () => {
          if (
            data.length ===
            0
          ) {
            return 0;
          }

          return Math.max(
            ...data.map(
              (
                item,
              ) =>
                item.count,
            ),
          );
        },
        [
          data,
        ],
      );


    /**
     * 최근 7일 총 방문
     */
    const totalCount =
      useMemo(
        () =>
          data.reduce(
            (
              total,
              item,
            ) =>
              total +
              item.count,
            0,
          ),
        [
          data,
        ],
      );


    /**
     * 일평균 방문
     */
    const averageCount =
      useMemo(
        () => {
          if (
            data.length ===
            0
          ) {
            return 0;
          }

          return Math.round(
            totalCount /
              data.length,
          );
        },
        [
          data.length,
          totalCount,
        ],
      );


    /**
     * 가장 방문자가 많았던 날
     */
    const peakItem =
      useMemo(
        () => {
          if (
            data.length ===
            0
          ) {
            return null;
          }

          return data.reduce(
            (
              previous,
              current,
            ) =>
              current.count >
              previous.count
                ? current
                : previous,
          );
        },
        [
          data,
        ],
      );


    return (
      <section className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">

        {/* =================================================
            Header
            ================================================= */}
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-4 sm:px-5">

          <div className="flex items-center gap-3">

            <span className="grid h-10 w-10 place-items-center rounded-xl bg-main/10 text-main">
              <TrendingUp
                size={20}
              />
            </span>


            <div>

              <h2 className="text-base font-bold text-gray-900">
                최근 7일 방문 추이
              </h2>


              <p className="mt-0.5 text-xs text-gray-400">
                최근 일주일간 매장 방문 흐름입니다.
              </p>

            </div>

          </div>


          <div className="hidden text-right sm:block">

            <p className="text-[11px] text-gray-400">
              일평균
            </p>


            <p className="mt-0.5 text-sm font-bold text-gray-900">
              {averageCount.toLocaleString()}
              명
            </p>

          </div>

        </div>


        {/* =================================================
            Empty
            ================================================= */}
        {data.length ===
        0 ? (

          <div className="flex min-h-[270px] flex-col items-center justify-center px-5 text-center">

            <span className="grid h-12 w-12 place-items-center rounded-full bg-gray-100 text-gray-400">

              <Users
                size={22}
              />

            </span>


            <strong className="mt-3 text-sm text-gray-700">
              방문 데이터가 없습니다.
            </strong>


            <p className="mt-1 text-xs text-gray-400">
              방문 기록이 생성되면 최근 7일 추이가 표시됩니다.
            </p>

          </div>

        ) : (

          <>
            {/* =================================================
                Summary
                ================================================= */}
            <div className="grid grid-cols-3 border-b border-gray-100">

              <div className="px-3 py-3 text-center sm:px-4">

                <p className="text-[10px] text-gray-400 sm:text-xs">
                  7일 방문
                </p>


                <p className="mt-1 text-base font-black text-gray-900 sm:text-lg">
                  {totalCount.toLocaleString()}
                  <span className="ml-0.5 text-xs font-normal text-gray-400">
                    명
                  </span>
                </p>

              </div>


              <div className="border-x border-gray-100 px-3 py-3 text-center sm:px-4">

                <p className="text-[10px] text-gray-400 sm:text-xs">
                  일평균
                </p>


                <p className="mt-1 text-base font-black text-gray-900 sm:text-lg">
                  {averageCount.toLocaleString()}
                  <span className="ml-0.5 text-xs font-normal text-gray-400">
                    명
                  </span>
                </p>

              </div>


              <div className="px-3 py-3 text-center sm:px-4">

                <p className="text-[10px] text-gray-400 sm:text-xs">
                  최고 방문
                </p>


                <p className="mt-1 text-base font-black text-main sm:text-lg">
                  {(
                    peakItem
                      ?.count ??
                    0
                  ).toLocaleString()}
                  <span className="ml-0.5 text-xs font-normal text-gray-400">
                    명
                  </span>
                </p>

              </div>

            </div>


            {/* =================================================
                Chart
                ================================================= */}
            <div className="px-4 pb-5 pt-6 sm:px-5">

              <div className="flex h-52 items-end justify-between gap-2 sm:gap-3">

                {data.map(
                  (
                    item,
                    index,
                  ) => {
                    /**
                     * 최소 높이 6%
                     *
                     * 방문자 수가 0이어도
                     * 아주 작은 막대를 보여줌
                     */
                    const heightPercent =
                      maxCount >
                      0
                        ? Math.max(
                            (
                              item.count /
                              maxCount
                            ) *
                              100,
                            6,
                          )
                        : 6;


                    const isPeak =
                      peakItem !==
                        null &&
                      item.count ===
                        peakItem.count;


                    return (
                      <div
                        key={`${item.date}-${index}`}
                        className="flex h-full min-w-0 flex-1 flex-col justify-end"
                      >

                        {/* Count */}
                        <div className="mb-2 text-center">

                          <span
                            className={`
                              text-[10px] font-bold
                              sm:text-xs
                              ${
                                isPeak
                                  ? 'text-main'
                                  : 'text-gray-500'
                              }
                            `}
                          >
                            {item.count.toLocaleString()}
                          </span>

                        </div>


                        {/* Bar container */}
                        <div className="flex min-h-0 flex-1 items-end justify-center">

                          <div className="flex h-full w-full max-w-9 items-end rounded-t-lg bg-gray-50 sm:max-w-11">

                            <div
                              className={`
                                w-full rounded-t-lg
                                transition-all
                                duration-300
                                ${
                                  isPeak
                                    ? 'bg-main'
                                    : 'bg-main/30'
                                }
                              `}
                              style={{
                                height:
                                  `${heightPercent}%`,
                              }}
                              title={`${item.date} ${item.count.toLocaleString()}명`}
                            />

                          </div>

                        </div>


                        {/* Date */}
                        <div className="mt-2 text-center">

                          <span
                            className={`
                              text-[10px]
                              sm:text-xs
                              ${
                                isPeak
                                  ? 'font-bold text-main'
                                  : 'text-gray-400'
                              }
                            `}
                          >
                            {
                              item.date
                            }
                          </span>

                        </div>

                      </div>
                    );
                  },
                )}

              </div>

            </div>


            {/* =================================================
                Footer
                ================================================= */}
            {peakItem && (

              <div className="border-t border-gray-100 bg-gray-50 px-4 py-3 sm:px-5">

                <p className="text-xs text-gray-500">
                  최근 7일 중{' '}
                  <strong className="text-gray-800">
                    {peakItem.date}
                  </strong>
                  에 방문자가 가장 많았으며{' '}
                  <strong className="text-main">
                    {peakItem.count.toLocaleString()}
                    명
                  </strong>
                  이 방문했습니다.
                </p>

              </div>

            )}

          </>
        )}

      </section>
    );
  };


export default VisitorTrendSection;